import { useCallback, useRef, useState } from 'react';

/** Записи Listening в моке на компьютере (components/ielts/MockListeningAudio.tsx, 21.10.2026).
 *  Части не ждут друг друга (правка владельца 21.10.2026): любую можно включить в любой момент.
 *  started — какие части уже включали (ученик или автозапуск), done — какие доиграли до конца,
 *  nonces — сигнал «включить»: доигравшая часть включает следующую, только если ту ещё не трогали.
 *
 *  ⚠️ ПРОСЛУШАННОЕ ЖИВЁТ В ПОПЫТКЕ (техдолг 432, 23.10.2026): место каждой части и «дослушано» —
 *  в `section_states.listening_audio` попытки. Раньше очередь жила только на странице, и F5 давал
 *  включить звучавшую часть заново. Сохраняем сразу на паузе и в конце части, пока звук идёт —
 *  не чаще раза в SAVE_EVERY_MS: после F5 часть откатится назад не больше чем на это время,
 *  вперёд — никогда. Две вкладки одной попытки не сводятся (техдолг 435). */

/** Как часто сохранять место, пока звук идёт. */
export const SAVE_EVERY_MS = 5000;

/** Ключ в section_states попытки. */
export const AUDIO_STATE_KEY = 'listening_audio';

type PartState = { pos: number; done: boolean };

/** Сохранённое состояние частей из попытки — без доверия к форме: чужое и битое пропускаем. */
export function readAudioStates(states: Record<string, unknown> | null | undefined): Record<number, PartState> {
  const raw = states?.[AUDIO_STATE_KEY];
  const out: Record<number, PartState> = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const i = Number(k);
    if (!Number.isInteger(i) || i < 0 || !v || typeof v !== 'object') continue;
    const { pos, done } = v as { pos?: unknown; done?: unknown };
    out[i] = {
      pos: typeof pos === 'number' && Number.isFinite(pos) && pos > 0 ? pos : 0,
      done: done === true,
    };
  }
  return out;
}

/** onChange — сохранить попытку (страница мока шлёт PUT progress со snapshot()). */
export function useMockAudioQueue(onChange?: () => void) {
  const started = useRef(new Set<number>());
  const [done, setDone] = useState<ReadonlySet<number>>(() => new Set());
  const doneRef = useRef<ReadonlySet<number>>(done);
  const [nonces, setNonces] = useState<Record<number, number>>({});
  // Место каждой части, секунды. В ref: меняется четыре раза в секунду, перерисовка не нужна.
  const positions = useRef<Record<number, number>>({});
  // Прочие ключи section_states попытки — отдаём обратно как были.
  const base = useRef<Record<string, unknown>>({});
  const lastSave = useRef(0);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const markDone = useCallback((next: ReadonlySet<number>) => {
    doneRef.current = next;
    setDone(next);
  }, []);
  const saveNow = useCallback(() => {
    lastSave.current = Date.now();
    onChangeRef.current?.();
  }, []);

  /** Восстановить из попытки (начало мока или возврат после F5). */
  const restore = useCallback((states: Record<string, unknown> | null | undefined) => {
    base.current = { ...(states ?? {}) };
    const saved = readAudioStates(states);
    const nextDone = new Set<number>();
    positions.current = {};
    started.current = new Set();
    for (const [k, s] of Object.entries(saved)) {
      const i = Number(k);
      positions.current[i] = s.pos;
      if (s.done) nextDone.add(i);
      if (s.done || s.pos > 0) started.current.add(i);
    }
    markDone(nextDone);
  }, [markDone]);

  const playing = useCallback((i: number) => {
    started.current.add(i);
  }, []);
  const time = useCallback((i: number, sec: number) => {
    if (!Number.isFinite(sec) || sec <= 0) return;
    positions.current[i] = sec;
    if (Date.now() - lastSave.current >= SAVE_EVERY_MS) saveNow();
  }, [saveNow]);
  const ended = useCallback((i: number, count: number) => {
    markDone(new Set(doneRef.current).add(i));
    saveNow();
    const next = i + 1;
    if (next < count && !started.current.has(next)) {
      started.current.add(next);
      setNonces((n) => ({ ...n, [next]: Date.now() }));
    }
  }, [markDone, saveNow]);
  /** С какого места включать часть: при каждом показе плеера (F5, возврат с вкладки Reading). */
  const startAt = useCallback((i: number) => positions.current[i] ?? 0, []);
  /** section_states для сохранения попытки. */
  const snapshot = useCallback((): Record<string, unknown> => {
    const parts: Record<string, PartState> = {};
    const idx = new Set([...Object.keys(positions.current).map(Number), ...doneRef.current]);
    for (const i of idx) {
      parts[String(i)] = { pos: Math.round((positions.current[i] ?? 0) * 10) / 10, done: doneRef.current.has(i) };
    }
    return { ...base.current, [AUDIO_STATE_KEY]: parts };
  }, []);

  return { done, nonces, playing, time, paused: saveNow, ended, startAt, restore, snapshot };
}

export type MockAudioQueue = ReturnType<typeof useMockAudioQueue>;
